/**
 * TextAudioBlock Component
 * Displays synchronized text and audio with real-time highlighting
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Activity, Download } from 'lucide-react';
import { FormattedTextBlock } from './FormattedTextBlock';
import type { StickyAudioMap } from '../../utils/stickyAudio';
import { TEXT_AUDIO_PAUSE_FOR_TIP_EVENT, TEXT_AUDIO_TIP_POINTER_EVENT } from '../../utils/textAudioTipPlayback';
import type { LessonTypographyScope } from '../../constants/learnerPersianFonts';
import { useTheme } from '../../hooks/useTheme';
import { AudioVisualizer } from '../shared/AudioVisualizer';
import { useResponsive } from '../shared/MobileResponsive';
import { isCrossOriginMediaUrl, safeAudioPlay } from '../../utils/audioPlaybackReady';
import { clearAudioBarChromeEntry, syncAudioBarChromeEntry } from '../../utils/audioBarChromeState';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';

interface AudioSyncSection {
  startTime: number;
  endTime: number;
  textRange: {
    start: number;
    end: number;
  };
}

/** Extra bottom inset (px) for app chrome when waveform strip is expanded (matches max-h strip). */
const TEXT_AUDIO_VIZ_CHROME_PX = 88;

/** Hysteresis (px) so pin/unpin does not oscillate at the block-end threshold. */
const TEXT_AUDIO_PIN_HYSTERESIS_PX = 20;

const SPEED_OPTIONS = [2, 1.5, 1, 0.7, 0.5] as const;

const DOWNLOAD_DISABLED_MESSAGE =
  'Downloads are disabled for this audio file. The instructor or admin has not allowed downloading it.';

interface TextAudioBlockProps {
  textContent: string;
  textFormatting?: any;
  audioUrl: string;
  audioSyncData?: string; // JSON string of AudioSyncSection[]
  tips?: any[];
  /** Same as audio blocks: admin "Allow learners to download this audio". */
  isDownloadable?: boolean;
  renderTipIcon?: (tip: any, position?: 'top' | 'bottom') => React.ReactNode;
  lessonTypography?: LessonTypographyScope;
  /** Global learner search: mark matched substring in the text body. */
  searchHighlightTerm?: string;
  stickyAudioMap?: StickyAudioMap;
  stickyAudioEnabled?: boolean;
  wordHelpBlockId?: number;
  /** Admin preview: force mobile audio UI regardless of viewport width */
  forceMobileLayout?: boolean;
  /** Admin preview: omit mobile bottom-banner offset (bar still sticky within block). */
  containedAudioBar?: boolean;
  /** Block-authored direction (isolates from UI locale). */
  contentDirection?: 'ltr' | 'rtl';
}

export const TextAudioBlock: React.FC<TextAudioBlockProps> = ({
  textContent,
  textFormatting,
  audioUrl,
  audioSyncData,
  tips = [],
  isDownloadable = false,
  renderTipIcon,
  lessonTypography,
  searchHighlightTerm,
  stickyAudioMap,
  stickyAudioEnabled = false,
  wordHelpBlockId,
  forceMobileLayout = false,
  containedAudioBar = false,
  contentDirection,
}) => {
  const { isDark } = useTheme();
  const { isMobile: isMobileLayout } = useResponsive();
  const useMobileAudioUi = forceMobileLayout || isMobileLayout;
  const downloadTooltip = isDownloadable ? 'Download audio' : DOWNLOAD_DISABLED_MESSAGE;
  const downloadAllowed = isDownloadable;
  const audioRef = useRef<HTMLAudioElement>(null);
  const blockContainerRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const textAudioTextRef = useRef<HTMLDivElement>(null);
  const tipAccessLayerRef = useRef<HTMLDivElement>(null);
  const highlightOverlayRef = useRef<HTMLDivElement>(null);
  const tipPointerActiveRef = useRef(false);
  const audioBarSlotRef = useRef<HTMLDivElement>(null);
  const audioBarChromeIdRef = useRef(Symbol('text-audio-bar-chrome'));
  const pinBarToViewportRef = useRef(false);
  const speedDropdownRefMobile = useRef<HTMLDivElement>(null);
  const speedDropdownRefDesktop = useRef<HTMLDivElement>(null);
  const downloadHintTimerRef = useRef<number | null>(null);
  const renderTipIconRef = useRef(renderTipIcon);
  renderTipIconRef.current = renderTipIcon;
  const stableRenderTipIcon = React.useCallback(
    (tip: any, position?: string) => renderTipIconRef.current?.(tip, position),
    []
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isRepeating, setIsRepeating] = useState(false);
  const [highlightedRanges, setHighlightedRanges] = useState<Array<{ start: number; end: number }>>([]);
  const [hasEnded, setHasEnded] = useState(false);
  const [bannerCollapsed, setBannerCollapsed] = useState(false);
  const [showAudioVisualization, setShowAudioVisualization] = useState(false);
  const [showSpeedDropdown, setShowSpeedDropdown] = useState(false);
  const [showDownloadHint, setShowDownloadHint] = useState(false);
  const [pinBarToViewport, setPinBarToViewport] = useState(false);

  useEffect(() => {
    const handleBottomBannerState = (event: Event) => {
      const customEvent = event as CustomEvent<{ collapsed?: boolean; isMobile?: boolean }>;
      setBannerCollapsed(customEvent.detail?.collapsed ?? false);
    };

    window.addEventListener('app:bottomBannerState', handleBottomBannerState as EventListener);
    window.dispatchEvent(new CustomEvent('app:requestBottomBannerState'));

    return () => {
      window.removeEventListener('app:bottomBannerState', handleBottomBannerState as EventListener);
    };
  }, []);

  useEffect(() => {
    if (containedAudioBar) {
      pinBarToViewportRef.current = false;
      setPinBarToViewport(false);
      return;
    }

    const textEl = textContainerRef.current;
    const blockEl = blockContainerRef.current;
    const slotEl = audioBarSlotRef.current;
    if (!textEl || !blockEl) return;

    const updatePin = () => {
      const textRect = textEl.getBoundingClientRect();
      const vh = window.innerHeight;
      const bottomInsetPx = !bannerCollapsed ? 68 : 0;
      const visibleBottom = vh - bottomInsetPx;
      const baseBarPadPx = useMobileAudioUi ? 88 : 60;
      const vizExtraPx = showAudioVisualization ? TEXT_AUDIO_VIZ_CHROME_PX : 0;
      const barReservePx = baseBarPadPx + vizExtraPx;
      const h = TEXT_AUDIO_PIN_HYSTERESIS_PX;
      const currentlyPinned = pinBarToViewportRef.current;

      // Stable block-end Y: never derived from layout that changes when pin toggles.
      const blockEndY = textRect.bottom + barReservePx;

      const inReadingBand =
        textRect.top < visibleBottom - barReservePx && textRect.bottom > 96;

      let nextPinned = currentlyPinned;
      if (currentlyPinned) {
        // Block end reached while reading downward → revert to in-flow bar at block bottom.
        if (blockEndY <= visibleBottom - h) {
          nextPinned = false;
        } else if (!inReadingBand) {
          // Block scrolled out of the reading band (up past block 1 or down past the block).
          nextPinned = false;
        }
      } else if (
        inReadingBand &&
        blockEndY > visibleBottom + h
      ) {
        nextPinned = true;
      }

      if (nextPinned !== currentlyPinned) {
        pinBarToViewportRef.current = nextPinned;
        setPinBarToViewport(nextPinned);
      }
    };

    updatePin();
    const observer = new IntersectionObserver(() => updatePin(), {
      threshold: [0, 0.25, 0.5, 0.75, 1]
    });
    observer.observe(textEl);
    if (slotEl) observer.observe(slotEl);
    window.addEventListener('scroll', updatePin, { passive: true });
    window.addEventListener('resize', updatePin);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updatePin);
      window.removeEventListener('resize', updatePin);
    };
  }, [containedAudioBar, useMobileAudioUi, bannerCollapsed, showAudioVisualization]);

  useEffect(() => {
    return () => {
      if (downloadHintTimerRef.current !== null) {
        window.clearTimeout(downloadHintTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (containedAudioBar) return undefined;

    const extraChromeBottomPx = showAudioVisualization ? TEXT_AUDIO_VIZ_CHROME_PX : 0;
    const pinned = pinBarToViewport;
    syncAudioBarChromeEntry(audioBarChromeIdRef.current, pinned, extraChromeBottomPx);

    return () => {
      clearAudioBarChromeEntry(audioBarChromeIdRef.current);
    };
  }, [showAudioVisualization, pinBarToViewport, containedAudioBar]);

  // Parse sync data - use sections exactly as stored in AudioSyncData
  const syncSections = React.useMemo(() => {
    if (!audioSyncData) return [];
    try {
      const parsed = JSON.parse(audioSyncData);
      if (!Array.isArray(parsed)) return [];
      return parsed as AudioSyncSection[];
    } catch {
      return [];
    }
  }, [audioSyncData]);

  // Parse text formatting
  const formatting = React.useMemo(() => {
    if (!textFormatting) return undefined;
    try {
      if (typeof textFormatting === 'string') {
        return JSON.parse(textFormatting);
      }
      return textFormatting;
    } catch {
      return undefined;
    }
  }, [textFormatting]);

  // ============================================================================
  // HIGHLIGHTING STATE MANAGEMENT
  // ============================================================================
  // Consolidated highlighting state - single source of truth
  type HighlightState = {
    ranges: Array<{ start: number; end: number }>;
    source: 'auto' | 'manual' | 'preserved';
    timestamp: number;
  };

  const highlightStateRef = useRef<HighlightState>({
    ranges: [],
    source: 'auto',
    timestamp: 0
  });

  // Find active sync section based on current time
  const findActiveSection = useCallback((
    time: number,
    sections: AudioSyncSection[],
    audioDuration: number,
    audioEnded: boolean
  ): AudioSyncSection | null => {
    if (sections.length === 0) return null;

    const lastSection = sections[sections.length - 1];
    if (!lastSection) return null;

    // Check if audio has ended - always highlight last section
    if (audioEnded) {
      return lastSection;
    }

    // Check if we're at or near the end of audio
    const isAtEnd = audioDuration > 0 && time >= audioDuration - 0.1;

    // Find active section from last to first (prefer later sections)
    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      if (!section) continue;

      const isLastSection = i === sections.length - 1;

      if (isLastSection) {
        // Last section: highlight if past start time or at end
        if (time >= section.startTime || isAtEnd) {
          return section;
        }
      } else {
        // Other sections: highlight if within time range
        if (time >= section.startTime && time <= section.endTime) {
          return section;
        }
      }
    }

    // Fallback: if past last section's start, use last section
    if (time >= lastSection.startTime) {
      return lastSection;
    }

    return null;
  }, []);

  // Update highlighting based on audio state
  useEffect(() => {
    const currentState = highlightStateRef.current;

    // Handle manual highlights (from text clicks)
    if (currentState.source === 'manual') {
      // Clear manual highlight when audio catches up to it
      if (isPlaying && audioRef.current) {
        const manualRange = currentState.ranges[0];
        if (manualRange) {
          const matchingSection = syncSections.find(s =>
            s.textRange.start === manualRange.start &&
            s.textRange.end === manualRange.end
          );
          if (matchingSection && Math.abs(audioRef.current.currentTime - matchingSection.startTime) < 0.1) {
            // Switch back to auto mode
            highlightStateRef.current = {
              ranges: currentState.ranges,
              source: 'auto',
              timestamp: Date.now()
            };
          }
        }
      }
      return; // Keep manual highlight until cleared above
    }

    // Auto highlighting based on current time
    if (syncSections.length === 0) {
      if (currentTime === 0 && !hasEnded) {
        highlightStateRef.current = {
          ranges: [],
          source: 'auto',
          timestamp: Date.now()
        };
        setHighlightedRanges([]);
      }
      return;
    }

    const activeSection = findActiveSection(currentTime, syncSections, duration, hasEnded);

    if (activeSection) {
      const newRanges = [activeSection.textRange];
      highlightStateRef.current = {
        ranges: newRanges,
        source: hasEnded ? 'preserved' : 'auto',
        timestamp: Date.now()
      };
      setHighlightedRanges(newRanges);
    } else if (currentTime === 0 && !hasEnded && currentState.source !== 'preserved') {
      // Only clear at the very beginning if audio hasn't ended
      highlightStateRef.current = {
        ranges: [],
        source: 'auto',
        timestamp: Date.now()
      };
      setHighlightedRanges([]);
    } else if (currentState.source === 'preserved' && currentState.ranges.length > 0) {
      // Preserve last highlight when audio stops
      setHighlightedRanges(currentState.ranges);
    }
  }, [currentTime, isPlaying, syncSections, hasEnded, duration, findActiveSection]);

  // Highlight color based on theme
  const highlightColor = isDark 
    ? 'rgba(99, 102, 241, 0.3)' // indigo with transparency for dark mode
    : 'rgba(99, 102, 241, 0.2)'; // indigo with transparency for light mode

  // OLD: Inject highlights into HTML string - NO LONGER USED (replaced with CSS overlay)
  // Keeping for reference but not used
  const _injectHighlightsIntoHTML_unused = useCallback((html: string, ranges: Array<{ start: number; end: number }>): string => {
    if (!html || ranges.length === 0) return html;

    // Sort ranges by start position (descending) to insert from end to start
    const sortedRanges = [...ranges].sort((a, b) => b.start - a.start);
    
    let modifiedHtml = html;
    const highlightClass = 'text-audio-highlight';
    // Style will be applied via CSS class, but we need background-color for the animation
    const highlightStyle = `background-color: ${highlightColor}; border-radius: 2px;`;

    // Insert highlight spans from end to start to preserve indices
    for (const range of sortedRanges) {
      const start = range.start;
      const end = range.end;
      
      if (start < 0 || end <= start || end > textContent.length) {
        continue;
      }

      // Find the text content in the HTML and wrap it with highlight span
      // We need to find the plain text position in the HTML string
      // This is tricky because HTML has tags, so we need to track plain text position
      let plainTextPos = 0;
      let htmlPos = 0;
      let startHtmlPos = -1;
      let endHtmlPos = -1;
      let inTag = false;

      // Find start and end positions in HTML
      // Skip tip icon markers (spans with data-tip-id) when counting plain text
      while (htmlPos < modifiedHtml.length) {
        const char = modifiedHtml[htmlPos];
        
        // Check if we're entering a tip icon span - count its text content but skip the tags
        // This preserves tip icon spans in the HTML while correctly calculating plain text positions
        if (char === '<' && modifiedHtml.substring(htmlPos, htmlPos + 50).includes('data-tip-id')) {
          // Find the opening tag end
          const tagEnd = modifiedHtml.indexOf('>', htmlPos);
          if (tagEnd !== -1) {
            // Skip the opening tag, but we need to count the text inside
            // Find the closing </span> tag for this tip span
            let searchPos = tagEnd + 1;
            let depth = 1;
            let tipTextStart = searchPos;
            
            while (searchPos < modifiedHtml.length && depth > 0) {
              if (modifiedHtml.substring(searchPos).startsWith('</span>')) {
                // Found closing tag - the text between tagEnd+1 and searchPos is the tip text
                const tipText = modifiedHtml.substring(tipTextStart, searchPos);
                // Count the plain text inside the tip span (skip nested tags)
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = tipText;
                const tipPlainText = tempDiv.textContent || '';
                plainTextPos += tipPlainText.length;
                
                htmlPos = searchPos + 7; // Skip '</span>'
                depth--;
                continue;
              } else if (modifiedHtml.substring(searchPos).startsWith('<span')) {
                const nextTagEnd = modifiedHtml.indexOf('>', searchPos);
                if (nextTagEnd !== -1) {
                  searchPos = nextTagEnd + 1;
                  depth++;
                } else {
                  searchPos++;
                }
              } else {
                searchPos++;
              }
            }
            continue;
          }
        }
        
        if (char === '<') {
          inTag = true;
        } else if (char === '>') {
          inTag = false;
        } else if (!inTag && char !== '&') {
          // Regular text character
          if (plainTextPos === start && startHtmlPos === -1) {
            startHtmlPos = htmlPos;
          }
          if (plainTextPos === end && endHtmlPos === -1) {
            endHtmlPos = htmlPos;
            break;
          }
          plainTextPos++;
        } else if (!inTag && char === '&') {
          // Handle HTML entities - skip them but don't count as text
          const entityEnd = modifiedHtml.indexOf(';', htmlPos);
          if (entityEnd !== -1) {
            htmlPos = entityEnd;
            continue;
          }
        }
        
        htmlPos++;
      }

      if (startHtmlPos !== -1 && endHtmlPos !== -1) {
        // Check if the highlight range contains tip icon markers - if so, preserve them
        const highlightedSection = modifiedHtml.substring(startHtmlPos, endHtmlPos);
        
        // Check if there are tip icon spans in the highlighted section
        // If so, we need to preserve them when wrapping with highlight span
        if (highlightedSection.includes('data-tip-id')) {
          // The tip icon spans are already in the HTML, so we can safely wrap them
          // The highlight span will wrap around the tip icon spans, preserving them
        }
        
        // Insert highlight span with fade-in animation
        const before = modifiedHtml.substring(0, startHtmlPos);
        const highlighted = modifiedHtml.substring(startHtmlPos, endHtmlPos);
        const after = modifiedHtml.substring(endHtmlPos);
        
        modifiedHtml = `${before}<span class="${highlightClass}" style="${highlightStyle}">${highlighted}</span>${after}`;
      }
    }

    return modifiedHtml;
  }, [textContent, highlightColor]);

  // ============================================================================
  // FORMATTING AND DISPLAY STATE
  // ============================================================================
  // Use a stable reference for formatting to prevent tip icon re-renders
  const formattingRef = useRef(formatting);
  useEffect(() => {
    formattingRef.current = formatting;
  }, [formatting]);

  // Sync playback highlights are drawn via CSS overlay — keep formatted HTML stable so tips stay open.
  useEffect(() => {
    const pauseForTip = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
      }
    };
    const onTipPointer = (event: Event) => {
      const active = (event as CustomEvent<{ active?: boolean }>).detail?.active;
      tipPointerActiveRef.current = Boolean(active);
    };
    window.addEventListener(TEXT_AUDIO_PAUSE_FOR_TIP_EVENT, pauseForTip);
    window.addEventListener(TEXT_AUDIO_TIP_POINTER_EVENT, onTipPointer);
    return () => {
      window.removeEventListener(TEXT_AUDIO_PAUSE_FOR_TIP_EVENT, pauseForTip);
      window.removeEventListener(TEXT_AUDIO_TIP_POINTER_EVENT, onTipPointer);
    };
  }, []);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setHasEnded(false); // Reset ended flag when playing again
      // Don't clear manual highlight immediately - let it persist until currentTime updates
      // This prevents brief flash of wrong section when playing after clicking
    };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);
      // Update highlight state to preserved mode with last section
      const audio = audioRef.current;
      if (audio && audio.duration && syncSections.length > 0) {
        setCurrentTime(audio.duration);
        const lastSection = syncSections[syncSections.length - 1];
        if (lastSection) {
          const range = lastSection.textRange;
          highlightStateRef.current = {
            ranges: [range],
            source: 'preserved',
            timestamp: Date.now()
          };
          setHighlightedRanges([range]);
        }
      }
    };

    const handleVolumeChange = () => {
      setVolume(audio.volume);
      setIsMuted(audio.muted);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('volumechange', handleVolumeChange);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [audioUrl, syncSections]);

  // Set playback speed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isRepeating;
    }
  }, [isRepeating]);

  const toggleRepeat = useCallback(() => {
    setIsRepeating((r) => !r);
  }, []);

  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl?.trim()) return;

    if (isPlaying) {
      audio.pause();
      return;
    }

    setHasEnded(false);
    try {
      await safeAudioPlay(audio);
    } catch (error) {
      console.error('TextAudioBlock playback failed:', error);
    }
  }, [isPlaying, audioUrl]);

  const handleStop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTime(0);
    setHasEnded(false);
  }, []);

  const handleSeek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(time, duration));
    setCurrentTime(audio.currentTime);
    // Reset ended flag when seeking
    setHasEnded(false);
    // Text clicks lock highlighting in 'manual' mode; slider/skip seeks should resume auto sync.
    if (highlightStateRef.current.source === 'manual') {
      highlightStateRef.current = {
        ...highlightStateRef.current,
        source: 'auto',
        timestamp: Date.now()
      };
    }
  }, [duration]);

  const handleSkipBackward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Math.max(0, audio.currentTime - 2);
    handleSeek(newTime);
  }, [handleSeek]);

  const handleSkipForward = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = Math.min(duration, audio.currentTime + 2);
    handleSeek(newTime);
  }, [duration, handleSeek]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setIsMuted(audio.muted);
    if (!audio.muted) {
      setVolume(audio.volume || 1);
    }
  }, []);

  const handleSpeedClick = useCallback(() => {
    let ci = (SPEED_OPTIONS as readonly number[]).indexOf(playbackSpeed);
    if (ci < 0) ci = 2;
    const nextIndex = (ci + 1) % SPEED_OPTIONS.length;
    const newSpeed = SPEED_OPTIONS[nextIndex];
    if (newSpeed !== undefined) {
      setPlaybackSpeed(newSpeed);
    }
    setShowSpeedDropdown(true);
  }, [playbackSpeed]);

  const handleSpeedSelect = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    setShowSpeedDropdown(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inMobile = speedDropdownRefMobile.current?.contains(target);
      const inDesktop = speedDropdownRefDesktop.current?.contains(target);
      if (!inMobile && !inDesktop) {
        setShowSpeedDropdown(false);
      }
    };
    if (showSpeedDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showSpeedDropdown]);

  const normalizedAudioUrl = React.useMemo(() => {
    if (!audioUrl) return '';
    const raw = audioUrl.trim();
    if (!raw) return '';
    if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
    if (raw.startsWith('http') || raw.startsWith('/uploads/')) {
      return normalizeAppHostedUploadUrl(raw);
    }
    if (!raw.startsWith('/')) {
      return normalizeAppHostedUploadUrl(`/uploads/audio/${raw}`);
    }
    return normalizeAppHostedUploadUrl(raw);
  }, [audioUrl]);

  const audioCrossOrigin = normalizedAudioUrl && isCrossOriginMediaUrl(normalizedAudioUrl)
    ? 'anonymous'
    : undefined;

  const handleDownload = useCallback(() => {
    if (!isDownloadable) return;
    const url = normalizedAudioUrl;
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = url.split('/').pop() || 'audio';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [isDownloadable, normalizedAudioUrl]);

  const showDownloadDisabledHint = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    if (downloadAllowed) return;
    event.preventDefault();
    event.stopPropagation();
    setShowDownloadHint(true);
    if (downloadHintTimerRef.current !== null) {
      window.clearTimeout(downloadHintTimerRef.current);
    }
    downloadHintTimerRef.current = window.setTimeout(() => {
      setShowDownloadHint(false);
    }, 2200);
  }, [downloadAllowed]);

  const isNearTipIcon = React.useCallback((clientX: number, clientY: number): boolean => {
    const host = textAudioTextRef.current;
    if (!host) return false;

    const pad = 18;
    const targets = host.querySelectorAll<HTMLElement>(
      '.text-audio-tip-access-layer .tip-icon-wrapper, .text-audio-tip-access-layer button'
    );
    for (const el of targets) {
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left - pad &&
        clientX <= rect.right + pad &&
        clientY >= rect.top - pad &&
        clientY <= rect.bottom + pad
      ) {
        return true;
      }
    }
    return false;
  }, []);

  // Handle text click - seek to clicked position and play
  const handleTextClick = useCallback((event: React.MouseEvent) => {
    if (!textContainerRef.current || syncSections.length === 0) {
      console.log('TextAudioBlock: handleTextClick - no container or sync sections');
      return;
    }

    if (isNearTipIcon(event.clientX, event.clientY)) {
      return;
    }
    
    // Don't handle clicks on tip icon buttons/wrappers - but allow clicks on the text itself
    const target = event.target as HTMLElement;
    // Only block clicks on the actual tip icon wrapper/button, not on text inside tip spans
    if (target.closest('button') || 
        target.closest('.tip-icon') || 
        target.closest('.tip-icon-wrapper') ||
        target.closest('[role="button"]')) {
      console.log('TextAudioBlock: handleTextClick - clicked on tip icon button, ignoring');
      return;
    }
    // Allow clicks on text even if it's inside a [data-tip-id] span - we want those to work
    
    // Stop event propagation to prevent interfering with tip icon clicks
    event.stopPropagation();

    const container = textContainerRef.current;
    if (!container) return;
    
    // Get the actual text content element (the one with formatted content or plain text)
    // Find the element that contains the actual text (not tip icons)
    const textElement =
      container.querySelector(
        '[id^="formatted-text-"], [id^="preview-text-"], .whitespace-pre-wrap, p'
      ) || container;
    let charPosition = 0;

    console.log('TextAudioBlock: handleTextClick - calculating position', {
      clientX: event.clientX,
      clientY: event.clientY,
      syncSectionsCount: syncSections.length,
      textContentLength: textContent.length,
      containerTextLength: container.textContent?.length
    });

    // Try to get range from click point (modern browsers)
    let range: Range | null = null;
    try {
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(event.clientX, event.clientY);
      } else if ((document as any).caretPositionFromPoint) {
        const pos = (document as any).caretPositionFromPoint(event.clientX, event.clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.setEnd(pos.offsetNode, pos.offset);
        }
      }
    } catch (e) {
      console.warn('TextAudioBlock: Error getting range from point', e);
    }

    if (range && range.startContainer) {
      // Calculate character position by walking through text nodes only
      // This ensures we count only plain text, not HTML tags
      try {
        const clickedNode = range.startContainer;
        const clickedOffset = range.startOffset;
        
        // Walk through text nodes and count characters
        // Use textElement instead of container to avoid counting tip icons
        const walker = document.createTreeWalker(
          textElement as Node,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              // Skip text nodes inside tip icon wrappers (the actual icon buttons)
              // But include text nodes inside [data-tip-id] spans (the text itself)
              const parent = node.parentElement;
              if (parent?.closest('.tip-icon-wrapper, button')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Include all other text nodes, including those inside [data-tip-id] spans
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );
        
        let node;
        while (node = walker.nextNode()) {
          if (node === clickedNode) {
            charPosition += clickedOffset;
            break;
          }
          charPosition += (node.textContent || '').length;
        }
        
        // Ensure charPosition doesn't exceed textContent length
        if (charPosition > textContent.length) {
          console.warn('TextAudioBlock: Calculated position exceeds textContent length, clamping', {
            charPosition,
            textContentLength: textContent.length
          });
          charPosition = Math.min(charPosition, textContent.length);
        }
        
        console.log('TextAudioBlock: handleTextClick - found position from range', {
          charPosition,
          startOffset: clickedOffset,
          nodeType: clickedNode.nodeType,
          textContent: clickedNode.textContent?.substring(0, 20)
        });
      } catch (e) {
        console.warn('TextAudioBlock: Error calculating position from range', e);
        // Fallback: use textContent length calculation
        const clickedNode = range.startContainer;
        const walker = document.createTreeWalker(
          textElement as Node,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              // Skip text nodes inside tip icon wrappers (the actual icon buttons)
              // But include text nodes inside [data-tip-id] spans (the text itself)
              const parent = node.parentElement;
              if (parent?.closest('.tip-icon-wrapper, button')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Include all other text nodes, including those inside [data-tip-id] spans
              return NodeFilter.FILTER_ACCEPT;
            }
          }
        );
        
        let node;
        while (node = walker.nextNode()) {
          if (node === clickedNode) {
            charPosition += range.startOffset;
            break;
          }
          charPosition += (node.textContent || '').length;
        }
        
        // Ensure charPosition doesn't exceed textContent length
        if (charPosition > textContent.length) {
          charPosition = Math.min(charPosition, textContent.length);
        }
      }
    } else {
      // Fallback: find the text node at click position
      const walker = document.createTreeWalker(
        textElement as Node,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: (node) => {
              // Skip text nodes inside tip icon wrappers (the actual icon buttons)
              // But include text nodes inside [data-tip-id] spans (the text itself)
              const parent = node.parentElement;
              if (parent?.closest('.tip-icon-wrapper, button')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Include all other text nodes, including those inside [data-tip-id] spans
              return NodeFilter.FILTER_ACCEPT;
            }
        }
      );
      
      const rect = container.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const clickY = event.clientY - rect.top;
      
      let node;
      let bestNode: Text | null = null;
      let bestOffset = 0;
      let minDistance = Infinity;

      // Find the text node closest to the click position
      while (node = walker.nextNode()) {
        const textNode = node as Text;
        try {
          const range = document.createRange();
          range.selectNodeContents(textNode);
          const textRect = range.getBoundingClientRect();
          
          if (textRect && textRect.width > 0) {
            const nodeX = textRect.left - rect.left;
            const nodeY = textRect.top - rect.top;
            const distance = Math.sqrt(
              Math.pow(clickX - nodeX, 2) + Math.pow(clickY - nodeY, 2)
            );
            
            // Check if click is within the text node's horizontal bounds
            if (clickX >= nodeX && clickX <= nodeX + textRect.width && distance < minDistance) {
              minDistance = distance;
              bestNode = textNode;
              // Estimate offset based on click position within the text node
              const textLength = textNode.textContent?.length || 0;
              const relativeX = clickX - nodeX;
              bestOffset = Math.max(0, Math.min(textLength, 
                Math.round((relativeX / textRect.width) * textLength)));
            }
          }
        } catch (e) {
          // Ignore errors for individual nodes
        }
      }

      if (bestNode) {
        // Calculate character position by walking through text nodes
        // This ensures we count only plain text, not HTML tags
        try {
          const walker2 = document.createTreeWalker(
            textElement as Node,
            NodeFilter.SHOW_TEXT,
            {
            acceptNode: (node) => {
              // Skip text nodes inside tip icon wrappers (the actual icon buttons)
              // But include text nodes inside [data-tip-id] spans (the text itself)
              const parent = node.parentElement;
              if (parent?.closest('.tip-icon-wrapper, button')) {
                return NodeFilter.FILTER_REJECT;
              }
              // Include all other text nodes, including those inside [data-tip-id] spans
              return NodeFilter.FILTER_ACCEPT;
            }
            }
          );
          let node2;
          while (node2 = walker2.nextNode()) {
            if (node2 === bestNode) {
              charPosition += bestOffset;
              break;
            }
            charPosition += (node2.textContent || '').length;
          }
          
          // Ensure charPosition doesn't exceed textContent length
          if (charPosition > textContent.length) {
            charPosition = Math.min(charPosition, textContent.length);
          }
          
          console.log('TextAudioBlock: handleTextClick - found position from fallback', {
            charPosition,
            bestOffset,
            textLength: bestNode.textContent?.length,
            textContentLength: textContent.length
          });
        } catch (e) {
          console.warn('TextAudioBlock: Error calculating position from fallback', e);
          return;
        }
      } else {
        console.warn('TextAudioBlock: handleTextClick - could not find text node at click position');
        return;
      }
    }

    // Verify the calculated position is within textContent bounds
    // Allow charPosition to equal textContent.length to handle clicks at the very end
    const plainTextLength = textContent.length;
    if (charPosition > plainTextLength) {
      console.warn('TextAudioBlock: handleTextClick - position out of bounds', {
        charPosition,
        plainTextLength,
        syncSectionsCount: syncSections.length
      });
      
      // Log sync section ranges for debugging
      if (syncSections.length > 0 && syncSections[0] && syncSections[syncSections.length - 1]) {
        console.log('TextAudioBlock: Sync section ranges:', {
          first: syncSections[0]!.textRange,
          last: syncSections[syncSections.length - 1]!.textRange,
          maxEnd: Math.max(...syncSections.map(s => s.textRange.end))
        });
      }
      return;
    }

    // Clamp charPosition to valid range for section matching
    const clampedPosition = Math.min(charPosition, plainTextLength);
    
    // SIMPLE ALGORITHM: Find the section that contains this character position
    // Check from last to first to handle overlaps (prefer later sections)
    let containingSection: AudioSyncSection | undefined;
    
    for (let i = syncSections.length - 1; i >= 0; i--) {
      const section = syncSections[i];
      if (!section) continue;
      
      const isLastSection = i === syncSections.length - 1;
      
      if (isLastSection) {
        // LAST SECTION: If clicking anywhere past its start, always match it
        if (clampedPosition >= section.textRange.start) {
          containingSection = section;
          break;
        }
      } else {
        // OTHER SECTIONS: Check if position is within the section's text range
        if (clampedPosition >= section.textRange.start && clampedPosition <= section.textRange.end) {
          containingSection = section;
          break;
        }
      }
    }
    
    // Fallback: If no section found but we're past last section's start, use last section
    if (!containingSection && syncSections.length > 0) {
      const lastSection = syncSections[syncSections.length - 1];
      if (lastSection && clampedPosition >= lastSection.textRange.start) {
        containingSection = lastSection;
      }
    }

    console.log('TextAudioBlock: handleTextClick - found section', {
      charPosition,
      plainTextLength,
      containingSection: containingSection ? {
        startTime: containingSection.startTime,
        endTime: containingSection.endTime,
        textRange: containingSection.textRange
      } : null,
      nearbySections: syncSections
        .map((s, idx) => ({ idx, range: s.textRange, startTime: s.startTime }))
        .filter(s => Math.abs(s.range.start - charPosition) < 100 || Math.abs(s.range.end - charPosition) < 100)
        .slice(0, 3)
    });

    if (containingSection) {
      const audio = audioRef.current;
      const wasPlaying = audio && !audio.paused;
      
      // Get the section's startTime and text range
      const seekTime = containingSection.startTime;
      const range = containingSection.textRange;
      
      // Set manual highlight
      highlightStateRef.current = {
        ranges: [range],
        source: 'manual',
        timestamp: Date.now()
      };
      setHighlightedRanges([range]);
      
      // Then seek audio to the section's startTime
      if (audio) {
        audio.currentTime = seekTime;
        setCurrentTime(seekTime);
        // Pause audio if it was playing - user clicked to seek, not to continue playing
        if (wasPlaying) {
          audio.pause();
        }
      }
      
      console.log('TextAudioBlock: handleTextClick - clicked section', {
        seekTime,
        range,
        charPosition,
        wasPlaying,
        sectionIndex: syncSections.indexOf(containingSection),
        totalSections: syncSections.length
      });
    } else {
      console.warn('TextAudioBlock: handleTextClick - no section found for position', {
        charPosition,
        plainTextLength,
        syncSectionsCount: syncSections.length,
        firstSectionRange: syncSections[0]?.textRange,
        lastSectionRange: syncSections[syncSections.length - 1]?.textRange
      });
    }
  }, [syncSections, handleSeek, togglePlayPause, isNearTipIcon]);

  // Format time for display
  const formatTime = useCallback((time: number): string => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Create a stable key that doesn't change with highlights to preserve tip state
  // This prevents FormattedTextBlock from re-rendering when highlights change, keeping tips open
  const tipsKey = React.useMemo(() => tips.map(t => t.id).join('-'), [tips]);
  
  const formattingKey = React.useMemo(() => {
    // Use a stable key based on content, not highlights, to preserve tip state
    // Tip icons maintain their identity via data-tip-id attributes even when HTML changes
    // Include tips and theme in the key to ensure proper rendering when tips or theme change
    return `textaudio-${textContent.substring(0, 20)}-${formatting ? 'formatted' : 'plain'}-tips-${tipsKey}-theme-${isDark ? 'dark' : 'light'}`;
  }, [textContent, formatting, tipsKey, isDark]);
  
  // Memoize tips to prevent unnecessary re-renders when highlights change
  const stableTips = React.useMemo(() => tips, [tipsKey]);

  // ============================================================================
  // HIGHLIGHT OVERLAY RENDERING
  // ============================================================================
  const previousRangesRef = React.useRef<string>('');
  const updateTimeoutRef = React.useRef<number | null>(null);

  const syncTipAccessLayer = React.useCallback(() => {
    if (tipPointerActiveRef.current) return;

    const host = textAudioTextRef.current;
    const layer = tipAccessLayerRef.current;
    if (!host || !layer) return;

    const hostRect = host.getBoundingClientRect();
    const tipSpans = host.querySelectorAll<HTMLElement>('span[data-tip-id]');

    tipSpans.forEach((tipSpan) => {
      const tipId = tipSpan.getAttribute('data-tip-id');
      let wrapper = tipSpan.querySelector<HTMLElement>('.tip-icon-wrapper');
      if (!wrapper && tipId) {
        wrapper = layer.querySelector<HTMLElement>(
          `.tip-icon-wrapper[data-tip-id="${tipId}"]`
        );
      }
      if (!wrapper) return;

      if (tipId) {
        wrapper.setAttribute('data-tip-id', tipId);
      }
      if (wrapper.parentElement !== layer) {
        layer.appendChild(wrapper);
      }

      const spanRect = tipSpan.getBoundingClientRect();
      const nextLeft = spanRect.left - hostRect.left + spanRect.width / 2;
      const nextTop = spanRect.top - hostRect.top - 5;
      wrapper.style.position = 'absolute';
      if (Math.abs(parseFloat(wrapper.style.left || '0') - nextLeft) > 0.5) {
        wrapper.style.left = `${nextLeft}px`;
      }
      if (Math.abs(parseFloat(wrapper.style.top || '0') - nextTop) > 0.5) {
        wrapper.style.top = `${nextTop}px`;
      }
      wrapper.style.transform = 'translateX(-50%)';
      wrapper.style.zIndex = '10';
      wrapper.style.pointerEvents = 'auto';
    });
  }, []);
  
  // Function to update highlight overlays
  const updateHighlightOverlays = React.useCallback(() => {
    if (!highlightOverlayRef.current || !textContainerRef.current) return;
    
    const overlay = highlightOverlayRef.current;
    const container = textContainerRef.current;
    const ranges = highlightStateRef.current.ranges;
    
    // Clear existing highlights
    overlay.innerHTML = '';
    
    if (ranges.length === 0) {
      return;
    }
    
    // Find the actual text element (the one that contains the rendered text)
    const textElement =
      container.querySelector(
        '[id^="formatted-text-"], [id^="preview-text-"], .whitespace-pre-wrap, p'
      ) || container;
    if (!textElement) return;
    
    // Temporarily remove tip icons from layout flow using CSS
    // Tip icons are now absolutely positioned, so they don't affect text layout
    // No need to hide or modify them during highlight calculation
    
    const containerRect = container.getBoundingClientRect();
    const textElementRect = (textElement as HTMLElement).getBoundingClientRect();
    const allRects: Array<{ left: number; top: number; width: number; height: number }> = [];
    
    // Create highlight spans for each range
    ranges.forEach((range) => {
      const { start, end } = range;
      // Use original textContent length for validation since ranges are based on original text
      if (start < 0 || end <= start || end > textContent.length) {
        console.warn('TextAudioBlock: Invalid range', { start, end, textLength: textContent.length });
        return;
      }
      
      // Find exact text nodes and character offsets for precise highlighting
      // We need to walk through text nodes and count characters to match plain text positions
      const walker = document.createTreeWalker(
        textElement as Node,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            // Skip text nodes inside tip icon wrappers (the actual icon buttons)
            // But include text nodes inside [data-tip-id] spans (the text itself)
            const parent = node.parentElement;
            if (parent?.closest('.tip-icon-wrapper, button')) {
              return NodeFilter.FILTER_REJECT;
            }
            // Include all other text nodes, including those inside [data-tip-id] spans
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );
      
      let charCount = 0;
      let firstNode: Node | null = null;
      let firstNodeOffset = 0;
      let lastNode: Node | null = null;
      let lastNodeOffset = 0;
      
      let node;
      while (node = walker.nextNode()) {
        const nodeText = node.textContent || '';
        const nodeLength = nodeText.length;
        const nodeStart = charCount;
        const nodeEnd = charCount + nodeLength;
        
        // Find first node that contains the start position
        if (firstNode === null && nodeStart <= start && nodeEnd > start) {
          firstNode = node;
          firstNodeOffset = start - nodeStart; // Offset within the node
        }
        
        // Find last node that contains the end position
        if (nodeStart < end && nodeEnd >= end) {
          lastNode = node;
          lastNodeOffset = end - nodeStart; // Offset within the node
        }
        
        charCount += nodeLength;
        
        // Stop if we've passed the end position
        if (nodeStart >= end) break;
      }
      
      // Verify we found the nodes
      if (!firstNode || !lastNode) {
        console.warn('TextAudioBlock: Could not find text nodes for range', {
          start,
          end,
          textLength: textContent.length,
          totalCharCount: charCount,
          foundFirst: !!firstNode,
          foundLast: !!lastNode,
          previewText: textContent.substring(Math.max(0, start - 10), Math.min(textContent.length, end + 10))
        });
        return;
      }
      
      // Create precise range using exact character offsets
      try {
        const textRange = document.createRange();
        
        // Clamp offsets to valid node bounds
        const firstNodeText = firstNode.textContent || '';
        const firstOffset = Math.max(0, Math.min(firstNodeOffset, firstNodeText.length));
        
        const lastNodeText = lastNode.textContent || '';
        const lastOffset = Math.max(0, Math.min(lastNodeOffset, lastNodeText.length));
        
        // Set start at exact character position within first node
        textRange.setStart(firstNode, firstOffset);
        
        // Set end at exact character position within last node
        textRange.setEnd(lastNode, lastOffset);
        
        // Verify the range matches expected text
        const rangeText = textRange.toString();
        const expectedText = textContent.substring(start, end);
        
        // Debug: Log if range doesn't match expected text
        if (rangeText.trim() !== expectedText.trim()) {
          console.warn('TextAudioBlock: Range text mismatch', {
            start,
            end,
            rangeText: rangeText.substring(0, 50),
            expectedText: expectedText.substring(0, 50),
            firstNodeOffset,
            lastNodeOffset,
            firstNodeText: firstNodeText.substring(0, 50),
            lastNodeText: lastNodeText.substring(0, 50),
            totalCharCount: charCount
          });
        }
        
        // Get all rects for this range (handles multi-line text)
        const rects = textRange.getClientRects();
        
        if (rects.length === 0) {
          console.warn('TextAudioBlock: No rects found for range', {
            start,
            end,
            rangeText,
            expectedText,
            firstNodeText: firstNodeText.substring(0, 50),
            lastNodeText: lastNodeText.substring(0, 50)
          });
          return;
        }
        
        // Calculate position relative to container
        const textOffsetLeft = textElementRect.left - containerRect.left;
        const textOffsetTop = textElementRect.top - containerRect.top;
        
        Array.from(rects).forEach((rect) => {
          // Calculate position relative to textElement, then add offset to container
          const relativeLeft = rect.left - textElementRect.left + textOffsetLeft;
          const relativeTop = rect.top - textElementRect.top + textOffsetTop;
          
          allRects.push({
            left: relativeLeft,
            top: relativeTop,
            width: rect.width,
            height: rect.height
          });
        });
      } catch (e) {
        console.warn('TextAudioBlock: Error creating highlight overlay', e, {
          start,
          end,
          firstNode: firstNode?.textContent?.substring(0, 50),
          lastNode: lastNode?.textContent?.substring(0, 50)
        });
      }
    });
    
    // Merge overlapping rects to avoid double highlighting
    const mergedRects = mergeOverlappingRects(allRects);
    
    // Create highlight overlay for each merged rect
    mergedRects.forEach((rect) => {
      const highlightSpan = document.createElement('span');
      highlightSpan.className = 'text-audio-highlight-overlay';
      highlightSpan.style.cssText = `
        position: absolute;
        left: ${rect.left}px;
        top: ${rect.top}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        background-color: ${highlightColor};
        border-radius: 2px;
        pointer-events: none;
        z-index: 1;
        transition: opacity 0.15s ease-in;
      `;
      overlay.appendChild(highlightSpan);
    });
  }, [highlightColor, textContent]);
  
  // Helper function to merge overlapping rects
  const mergeOverlappingRects = (rects: Array<{ left: number; top: number; width: number; height: number }>) => {
    if (rects.length === 0) return [];
    
    // Sort by top, then by left
    const sorted = [...rects].sort((a, b) => {
      if (Math.abs(a.top - b.top) < 2) {
        return a.left - b.left;
      }
      return a.top - b.top;
    });
    
    const merged: Array<{ left: number; top: number; width: number; height: number }> = [];
    
    for (const rect of sorted) {
      // Check if this rect overlaps with any existing merged rect
      let mergedWith = false;
      
      for (let i = 0; i < merged.length; i++) {
        const existing = merged[i];
        if (!existing) continue;
        
        // Check if rects are on the same line (within 2px) and overlap horizontally
        if (Math.abs(existing.top - rect.top) < 2 && 
            rect.left <= existing.left + existing.width + 1 &&
            rect.left + rect.width >= existing.left - 1) {
          // Merge: extend the existing rect
          const newLeft = Math.min(existing.left, rect.left);
          const newRight = Math.max(existing.left + existing.width, rect.left + rect.width);
          merged[i] = {
            left: newLeft,
            top: existing.top,
            width: newRight - newLeft,
            height: existing.height
          };
          mergedWith = true;
          break;
        }
      }
      
      if (!mergedWith) {
        merged.push({ ...rect });
      }
    }
    
    return merged;
  };
  
  // Update highlight overlay positions when ranges change (debounced to prevent flashing)
  React.useEffect(() => {
    // Clear any pending updates
    if (updateTimeoutRef.current) {
      cancelAnimationFrame(updateTimeoutRef.current);
    }
    
    // Debounce updates using requestAnimationFrame
    updateTimeoutRef.current = requestAnimationFrame(() => {
      const ranges = highlightStateRef.current.ranges;
      const rangesKey = ranges.map(r => `${r.start}-${r.end}`).join(',');
      
      // Only update if ranges actually changed
      if (previousRangesRef.current === rangesKey) {
        return;
      }
      previousRangesRef.current = rangesKey;
      
      updateHighlightOverlays();
    });
    
    return () => {
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
      }
    };
  }, [highlightedRanges, updateHighlightOverlays, stickyAudioEnabled, stickyAudioMap, formatting]);

  // Keep tip icons above highlight overlays once TipIconRenderer mounts wrappers.
  React.useEffect(() => {
    const id = requestAnimationFrame(() => syncTipAccessLayer());
    return () => cancelAnimationFrame(id);
  }, [formattingKey, stableTips, syncTipAccessLayer]);

  // Recalculate CSS overlay highlights when sticky-audio DOM updates
  React.useEffect(() => {
    if (highlightStateRef.current.ranges.length === 0) return;
    const id = requestAnimationFrame(() => updateHighlightOverlays());
    return () => cancelAnimationFrame(id);
  }, [stickyAudioEnabled, stickyAudioMap, formatting, updateHighlightOverlays]);
  
  // Recalculate highlights on window resize (debounced)
  const resizeTimeoutRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const handleResize = () => {
      // Clear any pending resize updates
      if (resizeTimeoutRef.current) {
        cancelAnimationFrame(resizeTimeoutRef.current);
      }
      
      // Debounce resize updates
      resizeTimeoutRef.current = requestAnimationFrame(() => {
        if (highlightStateRef.current.ranges.length > 0) {
          updateHighlightOverlays();
        }
        syncTipAccessLayer();
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        cancelAnimationFrame(resizeTimeoutRef.current);
      }
    };
  }, [updateHighlightOverlays, syncTipAccessLayer]);

  // Create highlighted text content
  const renderHighlightedText = () => {
    return (
      <>
        <div
          ref={textAudioTextRef}
          onClick={handleTextClick}
          style={{ cursor: 'pointer', position: 'relative' }}
          className="text-audio-block-text"
          onMouseDown={(e) => {
            const target = e.target as HTMLElement;
            if (
              target.closest('.sticky-audio-mark') ||
              target.closest('button') ||
              target.closest('.tip-icon') ||
              target.closest('.tip-icon-wrapper') ||
              target.closest('[role="button"]')
            ) {
              e.stopPropagation();
            }
          }}
        >
          <FormattedTextBlock
            key={formattingKey}
            textContent={textContent}
            formatting={formatting}
            tips={stableTips}
            {...(contentDirection ? { contentDirection } : {})}
            {...(lessonTypography ? { lessonTypography } : {})}
            {...(renderTipIcon ? { renderTipIcon: stableRenderTipIcon as (tip: any, position: string) => React.ReactNode } : {})}
            {...(searchHighlightTerm ? { searchHighlightTerm } : {})}
            stickyAudioMap={stickyAudioMap}
            stickyAudioEnabled={stickyAudioEnabled}
            {...(wordHelpBlockId != null ? { wordHelpBlockId } : {})}
          />
          <div
            ref={highlightOverlayRef}
            className="text-audio-highlight-overlay-container"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none'
            }}
          />
          <div
            ref={tipAccessLayerRef}
            className="text-audio-tip-access-layer"
            aria-hidden
          />
        </div>
      </>
    );
  };

  const audioBarBottomOffset =
    !containedAudioBar && !bannerCollapsed ? '4.25rem' : '0px';
  const vizExtraPx = showAudioVisualization ? TEXT_AUDIO_VIZ_CHROME_PX : 0;
  const barPinnedToViewport = !containedAudioBar && pinBarToViewport;
  const audioBarSlotHeight = containedAudioBar
    ? undefined
    : `calc(${useMobileAudioUi ? '5.5rem' : '3.75rem'} + ${vizExtraPx}px)`;

  return (
    <div ref={blockContainerRef} className="text-audio-block-container relative">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={normalizedAudioUrl || undefined}
        style={{ display: 'none' }}
        {...(audioCrossOrigin ? { crossOrigin: audioCrossOrigin } : {})}
      />

      {/* Text Content with Highlighting */}
      <div
        ref={textContainerRef}
        className="text-audio-block-text-content pb-3 font-persian"
        style={{
          minHeight: '200px'
        }}
      >
        {renderHighlightedText()}
      </div>

      {/* Reserved slot keeps block height stable while the bar toggles fixed vs in-flow. */}
      <div
        ref={audioBarSlotRef}
        className="relative w-full min-w-0 shrink-0 overflow-visible"
        style={audioBarSlotHeight ? { height: audioBarSlotHeight } : undefined}
      >
        <div
          className={
            containedAudioBar
              ? 'sticky bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg'
              : barPinnedToViewport
                ? 'fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg'
                : 'absolute bottom-0 left-0 right-0 z-10 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-md'
          }
          style={
            containedAudioBar || barPinnedToViewport
              ? { bottom: audioBarBottomOffset }
              : undefined
          }
        >
        <div
          className={`overflow-hidden transition-[max-height,opacity,margin] duration-300 ease-in-out ${
            showAudioVisualization
              ? 'max-h-[88px] opacity-100 border-b border-gray-200 dark:border-gray-700'
              : 'max-h-0 opacity-0 border-b-0 pointer-events-none'
          }`}
        >
          <div className="px-2 pt-2 pb-1 sm:px-3">
            <AudioVisualizer
              audioElement={audioRef.current}
              isPlaying={isPlaying}
              height={60}
              barCount={32}
              className="w-full"
            />
          </div>
        </div>
        <div className="text-audio-player-bar max-w-7xl mx-auto w-full min-w-0 px-2 py-2 sm:px-3 sm:py-2" dir="ltr">
          {/* Mobile: two rows; second row uses flex-1 so volume strip matches first row width */}
          <div className={`flex flex-col gap-2 w-full min-w-0 ${useMobileAudioUi ? '' : 'sm:hidden'}`}>
            <div className="flex w-full min-w-0 items-center gap-1.5">
              <button
                type="button"
                onClick={togglePlayPause}
                className="flex shrink-0 items-center justify-center w-9 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 shadow-sm"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                <span className="text-sm">{isPlaying ? '⏸' : '▶'}</span>
              </button>
              <button
                type="button"
                onClick={handleStop}
                disabled={!normalizedAudioUrl}
                className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                aria-label="Stop"
                title="Stop"
              >
                <span className="text-sm">⏹</span>
              </button>
              <button
                type="button"
                onClick={() => setShowAudioVisualization((v) => !v)}
                className={`flex shrink-0 items-center justify-center w-8 h-8 rounded-lg transition-colors duration-200 hover:scale-105 ${
                  showAudioVisualization
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                aria-label={showAudioVisualization ? 'Hide audio visualization' : 'Show audio visualization'}
                title={showAudioVisualization ? 'Hide waveform' : 'Show waveform'}
              >
                <Activity className="w-4 h-4" aria-hidden />
              </button>
              <div className="flex flex-1 min-w-0 items-center gap-1.5">
                <span className="text-[0.7rem] tabular-nums text-gray-600 dark:text-gray-400 shrink-0">
                  {formatTime(currentTime)}
                </span>
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={(e) => handleSeek(parseFloat(e.target.value))}
                  className="flex-1 min-w-0 h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / (duration || 1)) * 100}%, #e5e7eb ${(currentTime / (duration || 1)) * 100}%, #e5e7eb 100%)`,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  aria-label="Audio progress"
                />
                <span className="text-[0.7rem] tabular-nums text-gray-600 dark:text-gray-400 shrink-0">
                  {formatTime(duration)}
                </span>
              </div>
            </div>
            <div className="flex w-full min-w-0 items-center gap-1.5">
              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleSkipBackward}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  aria-label="Skip backward 2 seconds"
                >
                  <span className="text-sm">⏪</span>
                </button>
                <button
                  type="button"
                  onClick={handleSkipForward}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  aria-label="Skip forward 2 seconds"
                >
                  <span className="text-sm">⏩</span>
                </button>
                <button
                  type="button"
                  onClick={toggleRepeat}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    isRepeating
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                  aria-label={isRepeating ? 'Disable repeat' : 'Enable repeat'}
                  title={isRepeating ? 'Disable repeat' : 'Enable repeat'}
                >
                  <span className="text-sm">🔁</span>
                </button>
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-1">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors"
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  <span className="text-sm">{isMuted ? '🔇' : '🔊'}</span>
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="h-1.5 min-w-0 flex-1 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume) * 100}%, #e5e7eb ${(isMuted ? 0 : volume) * 100}%, #e5e7eb 100%)`,
                    WebkitAppearance: 'none',
                    appearance: 'none',
                  }}
                  aria-label="Volume"
                />
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="relative shrink-0" ref={speedDropdownRefMobile}>
                  <button
                    type="button"
                    onClick={handleSpeedClick}
                    disabled={!normalizedAudioUrl}
                    className={`flex items-center justify-center min-w-[2.75rem] h-8 px-2 rounded-lg font-medium text-xs transition-all duration-200 ${
                      playbackSpeed !== 1
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                    title={`Playback speed: ${playbackSpeed}x (click to change)`}
                    aria-label={`Playback speed: ${playbackSpeed}x`}
                  >
                    <span>{playbackSpeed}x</span>
                  </button>
                  {showSpeedDropdown && (
                    <div className="absolute bottom-full left-0 mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[5rem]">
                      {SPEED_OPTIONS.map((speed) => (
                        <button
                          key={speed}
                          type="button"
                          onClick={() => handleSpeedSelect(speed)}
                          className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                            playbackSpeed === speed
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span
                  className={`relative inline-flex rounded-lg ${!downloadAllowed ? 'cursor-help hover:bg-gray-200/40 dark:hover:bg-gray-600/30' : ''}`}
                  title={downloadTooltip}
                  onClick={showDownloadDisabledHint}
                >
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={!downloadAllowed}
                    className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                      downloadAllowed
                        ? 'bg-gray-100 hover:bg-gray-200 hover:scale-105 active:scale-95 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        : 'pointer-events-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 opacity-60'
                    }`}
                    aria-label={downloadTooltip}
                  >
                    <Download className="w-4 h-4" aria-hidden />
                  </button>
                  {showDownloadHint && !downloadAllowed && (
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 rounded-md bg-gray-900/95 px-2 py-1 text-[11px] leading-4 text-white shadow-lg">
                      {downloadTooltip}
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Desktop: single row */}
          <div className={`${useMobileAudioUi ? 'hidden' : 'hidden sm:flex'} sm:flex-row sm:items-center sm:flex-nowrap sm:gap-2 w-full min-w-0`}>
            <div className="flex flex-1 min-w-0 items-center gap-1">
              <button
                type="button"
                onClick={togglePlayPause}
                className="flex shrink-0 items-center justify-center w-9 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 shadow-sm"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                <span className="text-sm">{isPlaying ? '⏸' : '▶'}</span>
              </button>
              <button
                type="button"
                onClick={handleStop}
                disabled={!normalizedAudioUrl}
                className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                aria-label="Stop"
                title="Stop"
              >
                <span className="text-sm">⏹</span>
              </button>
              <button
                type="button"
                onClick={() => setShowAudioVisualization((v) => !v)}
                className={`flex shrink-0 items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                  showAudioVisualization
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                aria-label={showAudioVisualization ? 'Hide audio visualization' : 'Show audio visualization'}
                title={showAudioVisualization ? 'Hide waveform' : 'Show waveform'}
              >
                <Activity className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={handleSkipBackward}
                className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                aria-label="Skip backward 2 seconds"
              >
                <span className="text-sm">⏪</span>
              </button>
              <span className="min-w-[2.5rem] text-xs tabular-nums text-gray-600 dark:text-gray-400 shrink-0 font-medium">
                {formatTime(currentTime)}
              </span>
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={(e) => handleSeek(parseFloat(e.target.value))}
                className="flex-1 min-w-0 h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / (duration || 1)) * 100}%, #e5e7eb ${(currentTime / (duration || 1)) * 100}%, #e5e7eb 100%)`,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
                aria-label="Audio progress"
              />
              <span className="min-w-[2.5rem] text-xs tabular-nums text-gray-600 dark:text-gray-400 shrink-0 font-medium">
                {formatTime(duration)}
              </span>
              <button
                type="button"
                onClick={handleSkipForward}
                className="flex shrink-0 items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                aria-label="Skip forward 2 seconds"
              >
                <span className="text-sm">⏩</span>
              </button>
              <button
                type="button"
                onClick={toggleRepeat}
                className={`flex shrink-0 items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:scale-105 ${
                  isRepeating
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                aria-label={isRepeating ? 'Disable repeat' : 'Enable repeat'}
                title={isRepeating ? 'Disable repeat' : 'Enable repeat'}
              >
                <span className="text-sm">🔁</span>
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={toggleMute}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-105"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                <span className="text-sm">{isMuted ? '🔇' : '🔊'}</span>
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="h-1.5 w-14 shrink-0 rounded-lg appearance-none cursor-pointer bg-gray-200 dark:bg-gray-700 sm:w-16"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(isMuted ? 0 : volume) * 100}%, #e5e7eb ${(isMuted ? 0 : volume) * 100}%, #e5e7eb 100%)`,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
                aria-label="Volume"
              />
              <div className="relative shrink-0" ref={speedDropdownRefDesktop}>
                <button
                  type="button"
                  onClick={handleSpeedClick}
                  disabled={!normalizedAudioUrl}
                  className={`flex items-center justify-center min-w-[3rem] h-8 px-2 rounded-lg font-medium text-xs transition-all duration-200 hover:scale-105 ${
                    playbackSpeed !== 1
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                  title={`Playback speed: ${playbackSpeed}x (click to change)`}
                  aria-label={`Playback speed: ${playbackSpeed}x`}
                >
                  <span>{playbackSpeed}x</span>
                </button>
                {showSpeedDropdown && (
                  <div className="absolute bottom-full left-0 mb-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[5rem]">
                    {SPEED_OPTIONS.map((speed) => (
                      <button
                        key={speed}
                        type="button"
                        onClick={() => handleSpeedSelect(speed)}
                        className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          playbackSpeed === speed
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span
                className={`relative inline-flex rounded-lg ${!downloadAllowed ? 'cursor-help hover:bg-gray-200/40 dark:hover:bg-gray-600/30' : ''}`}
                title={downloadTooltip}
                onClick={showDownloadDisabledHint}
              >
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={!downloadAllowed}
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 ${
                    downloadAllowed
                      ? 'bg-gray-100 hover:bg-gray-200 hover:scale-105 active:scale-95 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                      : 'pointer-events-none bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 opacity-60'
                  }`}
                  aria-label={downloadTooltip}
                >
                  <Download className="w-4 h-4" aria-hidden />
                </button>
                {showDownloadHint && !downloadAllowed && (
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-56 rounded-md bg-gray-900/95 px-2 py-1 text-[11px] leading-4 text-white shadow-lg">
                    {downloadTooltip}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default TextAudioBlock;

